/**
 * AICopilotSettingsPanel — AI Copilot Configuration
 *
 * Settings: enable/disable, retrieval mode, max context size,
 * confidence threshold.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { saveCopilotConfig, updateCopilotLocal } from '../../../store/aiServicesSlice';
import { typography } from '~/theme';

interface Props {
  onSnack: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const RETRIEVAL_MODES = [
  { id: 'sql', label: 'SQL Only', desc: 'Direct database queries — fastest, no semantic search' },
  { id: 'hybrid', label: 'Hybrid (Recommended)', desc: 'Combines SQL + semantic search for best results' },
  { id: 'rag', label: 'RAG Only', desc: 'Full retrieval-augmented generation with vector search' },
];

const AICopilotSettingsPanel: React.FC<Props> = ({ onSnack }) => {
  const dispatch = useAppDispatch();
  const { copilot, saving, loading } = useAppSelector((s) => s.aiServices);
  const [dirty, setDirty] = useState(false);

  const handleChange = (field: string, value: string | number | boolean) => {
    dispatch(updateCopilotLocal({ [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await dispatch(saveCopilotConfig(copilot)).unwrap();
      setDirty(false);
      onSnack('AI Copilot settings saved', 'success');
    } catch (err) {
      onSnack(String(err), 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">AI Copilot Configuration</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the conversational AI assistant for radiology workflows
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            Save Changes
          </Button>
        </Box>

        <Divider />

        {/* Enable/Disable */}
        <Card variant="outlined">
          <CardHeader
            title="Copilot Status"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body1" fontWeight={typography.button.fontWeight}>
                  Enable AI Copilot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, the AI Copilot widget appears globally for all users,
                  providing conversational access to patient data, reports, and clinical reasoning.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={copilot.enabled ? 'Active' : 'Disabled'}
                  size="small"
                  color={copilot.enabled ? 'success' : 'default'}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={copilot.enabled}
                      onChange={(e) => handleChange('enabled', e.target.checked)}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Retrieval Mode */}
        <Card variant="outlined" sx={{ opacity: copilot.enabled ? 1 : 0.5 }}>
          <CardHeader
            title="Retrieval Mode"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }}
          />
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Retrieval Strategy</InputLabel>
              <Select
                value={copilot.retrieval_mode}
                label="Retrieval Strategy"
                onChange={(e) => handleChange('retrieval_mode', e.target.value)}
                disabled={!copilot.enabled}
              >
                {RETRIEVAL_MODES.map((mode) => (
                  <MenuItem key={mode.id} value={mode.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={typography.button.fontWeight}>{mode.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{mode.desc}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {RETRIEVAL_MODES.map((mode) => (
                <Grid item xs={12} md={4} key={mode.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: copilot.retrieval_mode === mode.id ? 'primary.main' : 'divider',
                      borderWidth: copilot.retrieval_mode === mode.id ? 2 : 1,
                      cursor: copilot.enabled ? 'pointer' : 'default',
                      opacity: copilot.enabled ? 1 : 0.6,
                    }}
                    onClick={() => copilot.enabled && handleChange('retrieval_mode', mode.id)}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">{mode.label}</Typography>
                          {copilot.retrieval_mode === mode.id && (
                            <Chip label="Selected" size="small" color="primary" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{mode.desc}</Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card variant="outlined" sx={{ opacity: copilot.enabled ? 1 : 0.5 }}>
          <CardHeader
            title="Advanced Settings"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }}
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Context Size (tokens)"
                  value={copilot.max_context_size}
                  onChange={(e) => handleChange('max_context_size', parseInt(e.target.value) || 4000)}
                  helperText="Maximum number of tokens included in LLM context window"
                  inputProps={{ min: 1000, max: 32000, step: 500 }}
                  disabled={!copilot.enabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Response Confidence Threshold: <strong>{copilot.confidence_threshold.toFixed(2)}</strong>
                </Typography>
                <Slider
                  value={copilot.confidence_threshold}
                  onChange={(_, v) => handleChange('confidence_threshold', v as number)}
                  min={0}
                  max={1}
                  step={0.05}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.3, label: '0.3' },
                    { value: 0.5, label: '0.5' },
                    { value: 0.7, label: '0.7' },
                    { value: 1, label: '1.0' },
                  ]}
                  disabled={!copilot.enabled}
                />
                <Typography variant="caption" color="text.secondary">
                  Responses below this confidence are flagged with a low-confidence warning.
                  Lower values show more results; higher values are more conservative.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info */}
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            The AI Copilot uses the model configured in the Model Routing table (ai_copilot service).
            It supports conversational context, PHI anonymization, and multi-source evidence retrieval.
          </Typography>
        </Box>

        {dirty && (
          <Chip label="Unsaved changes" color="warning" size="small" sx={{ alignSelf: 'flex-start' }} />
        )}
      </Stack>
    </Box>
  );
};

export default AICopilotSettingsPanel;
